import { useState } from "react"
import styled from "styled-components"

import { Badge, ListBox, spacing } from "@minuthq/meatball-ui-react"

import { ParadiseDetailsSection } from "src/components/Paradise/ParadiseDetailsLayout"
import { ParadiseDevicePointMessageDialog } from "src/components/Paradise/ParadiseDevices/ParadiseDeviceDetails/ParadiseDevicePointMessages/ParadiseDevicePointMessageDialog"
import { ParadisePager } from "src/components/Paradise/ParadisePager"
import { ParadiseTable } from "src/components/Paradise/ParadiseTable"
import { getPointMessageType } from "src/data/devices/logic/deviceLogic"
import { useFetchParadiseDevicePointMessages } from "src/data/paradise/paradiseDevices/queries/paradiseDeviceQueries"
import {
  IParadiseDevicePointMessage,
  IParadiseDevicePointMessagesFilter,
} from "src/data/paradise/paradiseDevices/types/paradiseDeviceQueryTypes"
import { useSearchParams } from "src/router/useSearchParams"
import { Filter } from "src/ui/Filter/Filter"
import { ColumnPopoverWrapper } from "src/ui/GridTable/useTableColumns/tableColumnStyles"
import {
  TableColumn,
  useTableColumns,
} from "src/ui/GridTable/useTableColumns/useTableColumns"
import FilterIcon from "src/ui/icons/calibrating.svg"
import Checkmark from "src/ui/icons/checkmark-base.svg"
import { formatUtcDate } from "src/utils/l10n"

type TParadiseDevicePointMessagesFilter = Partial<
  Pick<IParadiseDevicePointMessagesFilter, "message_type" | "from" | "to">
>

const LIMIT = 20

export function ParadiseDevicePointMessages({
  deviceId,
}: {
  deviceId: string
}) {
  const { searchParams, setSearchParams } = useSearchParams({
    keys: [
      {
        key: "message_type",
        type: "string",
        list: true,
      },
      {
        key: "messages_offset",
        type: "number",
      },
    ],
  })

  const [selectedPointMessage, setSelectedPointMessage] =
    useState<IParadiseDevicePointMessage | null>(null)

  const limit = LIMIT
  const offset = searchParams.messages_offset ?? 0
  const message_type =
    searchParams.message_type as TParadiseDevicePointMessagesFilter["message_type"]

  const fetchedPointMessages = useFetchParadiseDevicePointMessages({
    deviceId,
    filter: {
      limit,
      offset,
      message_type:
        (message_type?.join(
          ","
        ) as TParadiseDevicePointMessagesFilter["message_type"]) ?? undefined,
    },
    options: {
      keepPreviousData: true,
    },
  })

  const columns: TableColumn<IParadiseDevicePointMessage>[] = [
    {
      value: "message_type",
      label: "Message type",
      columnWidth: "min-content",
      renderCell: (message) => {
        return (
          <div>
            <div>
              <Badge size="x-small">
                {getPointMessageType(message.message_type)}
              </Badge>
            </div>
          </div>
        )
      },
      popoverContent: (
        <ColumnPopoverWrapper>
          <MessageTypeFilter
            messageType={message_type ?? []}
            setMessageType={(message_type) => {
              setSearchParams("message_type", message_type as string[], {
                replace: true,
              })
            }}
          />
        </ColumnPopoverWrapper>
      ),
      popoverIcon: FilterIcon,
    },
    {
      value: "received_at",
      label: "Received at",
      columnWidth: "min-content",
      renderCell: (message) => {
        return (
          <div>
            {formatUtcDate({
              date: new Date(message.timestamp_ms).toISOString(),
            })}
          </div>
        )
      },
    },
  ]

  const { headerElements, rows, templateColumns } = useTableColumns({
    columns,
    data: fetchedPointMessages.data?.messages ?? [],
  })

  return (
    <div>
      <ParadiseDetailsSection
        title={"Point messages"}
        totalCount={fetchedPointMessages.data?.paging.total_count}
      />

      {message_type && (
        <ParadiseDevicePointMessagesFilters
          messageType={message_type}
          setMessageType={(message_type) => {
            setSearchParams("message_type", message_type as string[], {
              replace: true,
            })
          }}
        />
      )}

      <ParadiseTable
        header={headerElements}
        rows={rows}
        isLoading={fetchedPointMessages.isLoading}
        templateColumns={templateColumns}
        onRowClick={(index) => {
          setSelectedPointMessage(
            fetchedPointMessages.data?.messages[index] ?? null
          )
        }}
        error={{
          hasError: fetchedPointMessages.isError,
          title: fetchedPointMessages.error?.message,
        }}
      />

      <PaginationWrapper>
        {fetchedPointMessages.data?.paging.total_count && (
          <ParadisePager
            limit={limit}
            offset={offset}
            setOffset={(offset) => {
              setSearchParams("messages_offset", offset)
            }}
            totalCount={fetchedPointMessages.data.paging.total_count}
          />
        )}
      </PaginationWrapper>

      {selectedPointMessage && (
        <ParadiseDevicePointMessageDialog
          open={!!selectedPointMessage}
          onClose={() => setSelectedPointMessage(null)}
          deviceId={deviceId}
          message={selectedPointMessage}
        />
      )}
    </div>
  )
}

function MessageTypeFilter({
  messageType,
  setMessageType,
}: {
  messageType: NonNullable<TParadiseDevicePointMessagesFilter["message_type"]>
  setMessageType: (
    messageType: TParadiseDevicePointMessagesFilter["message_type"] | null
  ) => void
}) {
  const options: NonNullable<
    TParadiseDevicePointMessagesFilter["message_type"]
  > = [
    "heartbeat_request",
    "heartbeat_response",
    "event_request",
    "event_response",
  ]

  function handleItemClick(
    item: NonNullable<
      TParadiseDevicePointMessagesFilter["message_type"]
    >[number]
  ) {
    if (!messageType) {
      setMessageType([item])
    } else if (messageType.includes(item)) {
      setMessageType(messageType.filter((i) => i !== item))
    } else {
      setMessageType([...messageType, item])
    }
  }

  return (
    <ListBox
      items={options.map((item) => ({
        id: item,
        content: item,
        suffix: messageType.includes(item) ? Checkmark : undefined,
        onClick: () => handleItemClick(item),
      }))}
    />
  )
}

const PaginationWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.L};
  justify-content: flex-end;
  min-width: 450px;
`

export function ParadiseDevicePointMessagesFilters({
  messageType,
  setMessageType,
}: {
  messageType: TParadiseDevicePointMessagesFilter["message_type"]
  setMessageType: (
    messageType: TParadiseDevicePointMessagesFilter["message_type"] | null
  ) => void
}) {
  return (
    <FilterWrapper>
      <Filter
        showAddButton={false}
        filters={[
          {
            id: "message_type",
            label: "Message type",
            value: messageType?.length.toString() ?? "N/A",
            active: !!messageType?.length,
            alwaysVisible: true,
            render: (
              <FilterContent>
                <MessageTypeFilter
                  messageType={messageType ?? []}
                  setMessageType={(message_type) => {
                    setMessageType(
                      message_type as TParadiseDevicePointMessagesFilter["message_type"]
                    )
                  }}
                />
              </FilterContent>
            ),
            onRemove: () => {
              setMessageType(null)
            },
          },
        ]}
      />
    </FilterWrapper>
  )
}

const FilterWrapper = styled.div`
  margin-top: ${spacing.M};
`

const FilterContent = styled.div`
  padding-inline: ${spacing.XS};
`
